// useMe fetches the authed user's profile from /api/me once on mount
// and caches it in memory (no localStorage, PHI hygiene). Used by the
// consumer shell to render the user's initials avatar and by the
// Home page for the personalized greeting.

import { useEffect, useRef, useState } from 'react';
import { useAuthedFetch } from './useAuthedFetch';

export interface Me {
  id: string;
  email: string;
  displayName: string | null;
  tier: 'FREE' | 'STANDARD' | 'PREMIUM' | 'FAMILY';
  cycle: 'MONTHLY' | 'ANNUAL' | null;
  accessibility: {
    fontSize?: 'base' | 'lg' | 'xl';
    highContrast?: boolean;
    reducedMotion?: boolean;
    hearingConcern?: boolean;
    textSizeChosen?: boolean;
    profileKind?: 'self' | 'caregiver';
    profileSubjectName?: string;
    profileKindChosen?: boolean;
    tourSeen?: boolean;
  };
  // Nullable since round-4 #1: a fresh user has no stored timezone
  // and the SPA PATCHes the browser-detected one on first /api/me load.
  // Existing code reads `me.timezone ?? <fallback>` where needed.
  timezone: string | null;
  wakeTime: string;
  bedTime: string;
  phoneE164: string | null;
  createdAt: string;
}

const FONT_SIZE_PX: Record<'base' | 'lg' | 'xl', string> = {
  base: '18px',
  lg: '22px',
  xl: '26px',
};

function applyPrefs(me: Me): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  const fontSize = (me.accessibility.fontSize as 'base' | 'lg' | 'xl' | undefined) ?? 'base';
  html.style.setProperty('--font-size-base', FONT_SIZE_PX[fontSize]);
  html.classList.toggle('theme-high-contrast', Boolean(me.accessibility.highContrast));
  html.classList.toggle('theme-reduced-motion', Boolean(me.accessibility.reducedMotion));
}

export function useMe(): { me: Me | null; loading: boolean; error: string | null } {
  const authedFetch = useAuthedFetch();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const tzAutoPatched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/me');
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          return;
        }
        const json = (await res.json()) as { ok: boolean; data?: Me };
        if (cancelled) return;
        if (json.ok && json.data) {
          setMe(json.data);
          applyPrefs(json.data);
          // One-time per session: align the user's stored timezone to the
          // browser's actual zone whenever the stored value looks like a
          // default (LA, UTC, empty) and the browser disagrees. Otherwise
          // the morning reminder fires at 11pm or 5am for anyone east of
          // Pacific. Focus-group round 2 (#8) flagged that UTC default in
          // particular was still leaking through for seeded / fresh users.
          const STALE_DEFAULTS = new Set(['America/Los_Angeles', 'UTC', '', 'Etc/UTC']);
          if (!tzAutoPatched.current && typeof Intl !== 'undefined') {
            tzAutoPatched.current = true;
            try {
              const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const storedTz = json.data.timezone ?? '';
              if (browserTz && browserTz !== storedTz && STALE_DEFAULTS.has(storedTz)) {
                await authedFetch('/api/me/timezone', {
                  method: 'PATCH',
                  body: JSON.stringify({ timezone: browserTz }),
                });
                if (!cancelled) setMe({ ...json.data, timezone: browserTz });
              }
            } catch {
              // Best-effort; failure is silent.
            }
          }
        } else {
          setError('Could not load profile');
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedFetch]);

  return { me, loading, error };
}

/** Derive a display first name from an email like "matthew@otchealthmart.com" -> "Matthew".
 * Returns '' when the local-part looks like a placeholder, test account, or
 * something that wouldn't be a real first name. The Home greeting falls back
 * to a name-less "Good evening." rather than rendering something like
 * "Good evening, E2e" or "Good evening, Admin" — which the persona focus
 * group flagged as the single most off-putting moment on the home screen.
 */
export function firstNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const seg = local.split(/[._-]/)[0] ?? local;
  if (!seg) return '';
  if (/\d/.test(seg)) return '';
  if (seg.length < 2) return '';
  if (
    /^(admin|test|demo|user|guest|root|system|noreply|info|support|hello|contact|webmaster|null|undefined|e2e)$/i.test(
      seg,
    )
  )
    return '';
  return seg.charAt(0).toUpperCase() + seg.slice(1).toLowerCase();
}
